import React, { useEffect, useState } from "react";

// STYLES
import "./ManageSubscription.scss";

// Components
import { Helmet } from "react-helmet-async";
import WithNavLayout from "../../layout/WithLayout/WithNavLayout";
import GenericModal from "../../ui/modals/GenericModal/GenericModal";
import GenericButton from "../../ui/buttons/GenericButtons/GenericButton";
import LoadingSpinner from "../../ui/modals/Spinners/LoadingSpinner";

// HOOKS
import useTimeout from "../../../hooks/lifecycleEvents/useTimeout";
import { useAuthContext } from "../../../hooks/context/AuthContext";

// TODO enumerate so states are more clear
const cancelToastTextOptions: Record<number, string> = {
    1: "Your subscription has been cancelled.",
    2: "There was an error cancelling your subscription, please contact support if this issue persists.",
};

interface IManageSubscriptionProps {
    // userEmail: string;
    // isPremium: boolean;
    // nextBillingDate: string;
}

const ManageSubscription: React.FC<IManageSubscriptionProps> = () =>
    //{
    // userEmail,
    // isPremium,
    // nextBillingDate,
    //},
    {
        const {
            token,
            //email,
            isPremium,
            activeStripeSub,
            getStripeSubscriptionInfo,
            postCancelSubscription,
            clearLocalSubscriptionStorage,
        } = useAuthContext();

        const [showCancellationModal, setShowCancellationModal] =
            useState<boolean>(false);
        const [cancelToastText, setCancelToastText] = useState<string>(
            cancelToastTextOptions[1],
        );
        const [showTopLevelToast, setShowTopLevelToast] =
            useState<boolean>(false);
        const [showLoadingSpinner, setShowLoadingSpinner] =
            useState<boolean>(false);

        const [activeSubCancelled, setActiveSubCancelled] =
            useState<boolean>(true);

        useEffect(() => {
            // console.log(
            //     "ACTIVE SUB",
            //     activeStripeSub,
            //     "\n\t",
            //     activeSubCancelled,
            // );
            if (activeStripeSub !== null) {
                if (activeStripeSub.status === "canceled") {
                    // this should be an enum, it may also be the case that additional status texts are needed (e.g. free trial)
                    setActiveSubCancelled(true);
                } else {
                    setActiveSubCancelled(false);
                }
            }
        }, [
            activeStripeSub,
            activeStripeSub?.subscription_id,
            activeStripeSub?.billing_plan,
            activeStripeSub?.status,
            activeStripeSub?.payment_method,
            activeStripeSub?.date_label,
            activeStripeSub?.date_unix,
            activeStripeSub?.date_utc,
            activeSubCancelled,
        ]);

        useTimeout(
            () => {
                setShowTopLevelToast(false);
            },
            showTopLevelToast ? 5000 : null,
        );

        const onCancelSubSuccess = () => {
            // leave up loading spinner -> get the new entitlement state
            getStripeSubscriptionInfo(
                token ?? "",
                () => {
                    // success
                    setShowLoadingSpinner(false);
                    setCancelToastText(cancelToastTextOptions[1]);
                    setShowTopLevelToast(true);
                },
                (error: string) => {
                    // fail
                    setShowLoadingSpinner(false);
                    setCancelToastText(cancelToastTextOptions[2]);
                    setShowTopLevelToast(true);
                },
            );
        };

        const onCancelSubFailure = (error: string) => {
            // lower loading spinner - show modal with error state
            setShowLoadingSpinner(false);
            setShowTopLevelToast(true);
            setCancelToastText(cancelToastTextOptions[2]);
        };

        const formatDate = () => {
            try {
                const formatted = new Intl.DateTimeFormat("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                }).format(new Date(activeStripeSub?.date_utc ?? ""));
                return formatted;
            } catch (e) {}
        };

        return (
            <>
                <Helmet>
                    <meta name="robots" content="noindex, nofollow" />
                </Helmet>

                <LoadingSpinner
                    className="GoPremiumLander__opaqueSpinner"
                    raised={showLoadingSpinner}
                    fullPage
                />
                <GenericModal
                    className="ManageSubscription__GenericModal"
                    show={showCancellationModal}
                    onHide={() => setShowCancellationModal(false)}
                    header="Are you Sure?"
                    body={
                        <p style={{ textAlign: "center" }}>
                            Without a Premium subscription you will no longer
                            have access to Heart Score, Heart Age, HRV, or
                            Sharable Reports.
                        </p>
                    }
                    footer={
                        <>
                            <GenericButton
                                text="Keep Premium Benefits"
                                onClick={() => setShowCancellationModal(false)}
                            />
                            <GenericButton
                                className="confirmCancel"
                                text="Confirm Cancel Subscription"
                                onClick={() => {
                                    setShowCancellationModal(false);
                                    setShowLoadingSpinner(true);
                                    postCancelSubscription(
                                        token ?? "",
                                        activeStripeSub?.subscription_id ?? "",
                                        onCancelSubSuccess,
                                        onCancelSubFailure,
                                    );
                                }}
                            />
                        </>
                    }
                />
                <WithNavLayout>
                    <div
                        className={`ManageSubscription__TopLevelToast ${
                            showTopLevelToast ? "visible" : ""
                        } ${
                            cancelToastText === cancelToastTextOptions[1]
                                ? "status_success"
                                : "status_fail"
                        }`}
                    >
                        <p>{cancelToastText}</p>
                    </div>
                    <div className="ManageSubscription">
                        {/* Title */}
                        <div className="ManageSubscription__Title">
                            <h1>Manage Subscription</h1>
                        </div>

                        {/* Content */}
                        <div className="ManageSubscription__SubscriptionDetails">
                            <div className="ManageSubscription__DetailRow">
                                <h5>Account Type</h5>
                                <p>{isPremium ? "Premium" : "Free"}</p>
                            </div>
                            <div className="ManageSubscription__DetailRowDivider" />
                            <div className="ManageSubscription__DetailRow">
                                <h5>Billing Plan</h5>
                                {/* TODO - derive from API */}
                                <p>{activeStripeSub?.billing_plan}</p>
                            </div>
                            <div className="ManageSubscription__DetailRowDivider" />
                            <div className="ManageSubscription__DetailRow">
                                <h5>Payment Method</h5>
                                {/* TODO - derive from API */}
                                <p>{activeStripeSub?.payment_method}</p>
                            </div>
                            <div className="ManageSubscription__DetailRowDivider" />
                            <div className="ManageSubscription__DetailRow">
                                <h5>
                                    {activeSubCancelled
                                        ? "Expires"
                                        : "Next Billing Date"}
                                </h5>
                                <p>
                                    {activeStripeSub?.date_utc
                                        ? new Intl.DateTimeFormat("en-US", {
                                              month: "short",
                                              day: "numeric",
                                              year: "numeric",
                                          }).format(
                                              new Date(
                                                  activeStripeSub.date_utc,
                                              ),
                                          )
                                        : ""}
                                </p>
                            </div>
                            {!isPremium || (isPremium && activeSubCancelled) ? (
                                <></>
                            ) : (
                                // Edge case handling
                                <div className="ManageSubscription__CancelSubscription">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowCancellationModal(true)
                                        }
                                    >
                                        Cancel Renewal
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </WithNavLayout>
            </>
        );
    };

export default ManageSubscription;
